import { proxy } from 'valtio'
import type { Node, ReactFlowInstance } from '@xyflow/react'
import { prependNodeAsset, readFirstNodeCanvasAsset } from '@/entities/node/canvas-asset'
import { deleteNode } from '@/entities/flow'
import { yCustomNodeDatas } from '@/shared/config/yjs-config'
import { addUploadedAssetsPage } from '@/entities/node/lib/list-node-operation'
import { getOp } from '@/shared/lib/op-registry'

export const nodeDragHoverStore = proxy({
  hoveredGalleryNodeId: null as string | null,
  draggedImageNodeId: null as string | null,
})

let hoverTimer: ReturnType<typeof setTimeout> | null = null
let currentIntersectingGalleryId: string | null = null

function getValidGalleryIntersections(reactFlow: ReactFlowInstance, node: Node): Node[] {
  return reactFlow.getIntersectingNodes(node).filter(n => {
    if (n.type !== 'gallery') return false

    const yNodeData = yCustomNodeDatas.get(n.id)
    if (yNodeData) {
      const status = yNodeData.get('status') as string | undefined
      if (status === 'pending' || status === 'running') {
        return false
      }

      const op = yNodeData.get('op') as string | undefined
      if (op) {
        const opMeta = getOp(op)
        if (opMeta?.isToolOp) {
          const pages = yNodeData.get('pages') as any[] | undefined
          let hasContent = false
          if (pages && pages.length > 0) {
            const lastPage = pages[pages.length - 1]
            if (
              (lastPage.assetRefs && lastPage.assetRefs.length > 0) ||
              (lastPage.failedRefs && lastPage.failedRefs.length > 0) ||
              (lastPage.failedRes && lastPage.failedRes.length > 0)
            ) {
              hasContent = true
            }
          }
          // 并且自身是空列表才禁用
          if (!hasContent) {
            return false
          }
        }
      }
    }

    const nodeAny = node as any
    const nAny = n as any

    const ax1 = nodeAny.internals?.positionAbsolute?.x ?? nodeAny.positionAbsolute?.x ?? node.position.x
    const ay1 = nodeAny.internals?.positionAbsolute?.y ?? nodeAny.positionAbsolute?.y ?? node.position.y
    const aw = node.measured?.width ?? node.width ?? 0
    const ah = node.measured?.height ?? node.height ?? 0
    const ax2 = ax1 + aw
    const ay2 = ay1 + ah

    const bx1 = nAny.internals?.positionAbsolute?.x ?? nAny.positionAbsolute?.x ?? n.position.x
    const by1 = nAny.internals?.positionAbsolute?.y ?? nAny.positionAbsolute?.y ?? n.position.y
    const bw = n.measured?.width ?? n.width ?? 0
    const bh = n.measured?.height ?? n.height ?? 0
    const bx2 = bx1 + bw
    const by2 = by1 + bh

    const overlapX = Math.max(0, Math.min(ax2, bx2) - Math.max(ax1, bx1))
    const overlapY = Math.max(0, Math.min(ay2, by2) - Math.max(ay1, by1))
    const overlapArea = overlapX * overlapY

    const areaA = aw * ah
    if (areaA === 0) return true

    // 拖拽节点与列表节点交叉 > 1/4
    return (overlapArea / areaA) > 0.25
  })
}

export function handleGlobalNodeDrag(reactFlow: ReactFlowInstance | null, node: Node) {
  if (!reactFlow) return

  if (node.type === 'image') {
    const yNodeDataMap = yCustomNodeDatas.get(node.id)
    const nodeData = yNodeDataMap ? yNodeDataMap.toJSON() : (node.data || {})
    const assetRefs = nodeData.assetRefs as unknown[] | undefined

    if (!assetRefs || !Array.isArray(assetRefs) || assetRefs.length === 0) {
      if (hoverTimer) {
        clearTimeout(hoverTimer)
        hoverTimer = null
      }
      currentIntersectingGalleryId = null
      nodeDragHoverStore.hoveredGalleryNodeId = null
      nodeDragHoverStore.draggedImageNodeId = null
      return
    }

    const intersections = getValidGalleryIntersections(reactFlow, node)
    if (intersections.length > 0) {
      const galleryId = intersections[0].id
      if (currentIntersectingGalleryId !== galleryId) {
        if (hoverTimer) clearTimeout(hoverTimer)
        currentIntersectingGalleryId = galleryId

        nodeDragHoverStore.hoveredGalleryNodeId = null
        nodeDragHoverStore.draggedImageNodeId = null

        hoverTimer = setTimeout(() => {
          nodeDragHoverStore.hoveredGalleryNodeId = galleryId
          nodeDragHoverStore.draggedImageNodeId = node.id
        }, 1000)
      }
    } else {
      if (hoverTimer) {
        clearTimeout(hoverTimer)
        hoverTimer = null
      }
      currentIntersectingGalleryId = null
      nodeDragHoverStore.hoveredGalleryNodeId = null
      nodeDragHoverStore.draggedImageNodeId = null
    }
  }
}

/**
 * Handle drop logic for dragging image nodes onto gallery nodes.
 * @returns true if the event was consumed
 */
export function handleGlobalNodeDragStop(reactFlow: ReactFlowInstance | null, node: Node): boolean {
  if (!reactFlow) return false

  if (hoverTimer) {
    clearTimeout(hoverTimer)
    hoverTimer = null
  }
  currentIntersectingGalleryId = null

  if (node.type === 'image') {
    const yNodeDataMap = yCustomNodeDatas.get(node.id)
    const nodeData = yNodeDataMap ? yNodeDataMap.toJSON() : (node.data || {})
    const assetRefs = nodeData.assetRefs as unknown[] | undefined

    if (!assetRefs || !Array.isArray(assetRefs) || assetRefs.length === 0) {
      nodeDragHoverStore.hoveredGalleryNodeId = null
      nodeDragHoverStore.draggedImageNodeId = null
      return false
    }

    const intersections = getValidGalleryIntersections(reactFlow, node)
    if (intersections.length > 0) {
      const galleryId = intersections[0].id
      const imageNodeId = node.id

      if (nodeDragHoverStore.hoveredGalleryNodeId === galleryId) {
        const yNodeDataMap = yCustomNodeDatas.get(imageNodeId)
        const nodeData = yNodeDataMap ? yNodeDataMap.toJSON() : {}
        const asset = readFirstNodeCanvasAsset(nodeData as Record<string, unknown>)

        if (asset) {
          prependNodeAsset(galleryId, asset.id)
          addUploadedAssetsPage(galleryId, [asset.id])
          deleteNode(imageNodeId)
        }

        nodeDragHoverStore.hoveredGalleryNodeId = null
        nodeDragHoverStore.draggedImageNodeId = null
        return true
      }
    }
  }

  nodeDragHoverStore.hoveredGalleryNodeId = null
  nodeDragHoverStore.draggedImageNodeId = null
  return false
}
